// src/pages/PortalSaaS/modules/ProjetosDosagem.js
import React, { useState, useEffect, useMemo, useCallback} from 'react';
import { useOutletContext } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, query, getDocs, doc, setDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import TabelaCRM from '../components/TabelaCRM';
import FormularioDinamico from '../components/FormularioDinamico';

const ProjetosDosagem = () => {
    const { workspaceData } = useOutletContext();
    const [view, setView] = useState('LIST'); 
    const [projetos, setProjetos] = useState([]);
    
    // Arrays para Chaves Estrangeiras (3FN)
    const [empresasList, setEmpresasList] = useState([]);
    const [obrasList, setObrasList] = useState([]); 
    
    const [isProcessing, setIsProcessing] = useState(false);
    const [editingProjeto, setEditingProjeto] = useState(null);

    // =========================================================================
    // 1. CARREGAMENTO DOS DADOS (CENÁRIO MULTITENANT)
    // =========================================================================
    const carregarDadosBase = useCallback(async () => {
        if (!workspaceData?.cnpj_completo) return;
        setIsProcessing(true);
        try {
            // A) Carrega Projetos de Dosagem
            const qProjetos = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_projetos_dosagem'), orderBy('created_at', 'desc'));
            const snapProjetos = await getDocs(qProjetos);
            setProjetos(snapProjetos.docs.map(d => ({ id: d.id, ...d.data() })));

            // B) Carrega Empresas (Clientes B2B)
            const qEmpresas = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_empresas'));
            const snapEmpresas = await getDocs(qEmpresas);
            const arrayEmpresas = snapEmpresas.docs.map(d => ({ id: d.id, ...d.data() }));
            setEmpresasList(arrayEmpresas.map(e => ({ label: e.nome_fantasia, value: e.id })));

            // C) Carrega Obras
            const qObras = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_obras'));
            const snapObras = await getDocs(qObras);
            
            // Mapeia Obras incluindo o nome da Empresa para evitar seleção incorreta
            const arrayObras = snapObras.docs.map(d => {
                const obra = d.data();
                const empresaDona = arrayEmpresas.find(emp => emp.id === obra.empresa_id);
                const nomeDona = empresaDona ? empresaDona.nome_fantasia : 'S/ Cliente';
                return {
                    label: `${obra.titulo} (Cliente: ${nomeDona})`,
                    value: d.id
                };
            });
            setObrasList(arrayObras);

        } catch (error) {
            console.error("Erro ao carregar dados do MeDiNa:", error);
            alert("Falha ao sincronizar o ecossistema de projetos.");
        } finally {
            setIsProcessing(false);
        }
    }, [workspaceData?.cnpj_completo]);

    useEffect(() => {
        carregarDadosBase();
    }, [carregarDadosBase]);

    // =========================================================================
    // 2. DEFINIÇÃO DA TABELA (VITRINE)
    // =========================================================================
    const colunas = [
        { header: 'ID Projeto', accessor: 'id', render: (row) => <strong style={{ color: '#0f172a' }}>{row.id}</strong> },
        { header: 'Título / Dosagem', accessor: 'titulo_projeto' },
        { header: 'Mistura', accessor: 'tipo_mistura', render: (row) => <span style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>{row.tipo_mistura}</span> },
        { header: 'Meta Tráfego (N)', accessor: 'numero_n_projeto' },
        { header: 'Status do Projeto', render: (row) => {
            let bgColor = '#e2e8f0';
            let textColor = '#475569';
            
            if (row.status === 'EM_ENSAIOS') { bgColor = '#e0f2fe'; textColor = '#0369a1'; }
            if (row.status === 'AGUARDANDO_PAGAMENTO') { bgColor = '#fef3c7'; textColor = '#d97706'; }
            if (row.status === 'LAUDO_LIBERADO') { bgColor = '#dcfce7'; textColor = '#16a34a'; }

            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <span style={{ background: bgColor, color: textColor, padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800', width: 'fit-content' }}>
                        {row.status.replace(/_/g, ' ')}
                    </span>
                    {/* Barra de Progresso Visual */}
                    <div style={{ width: '100%', background: '#e2e8f0', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ width: `${row.progresso}%`, background: textColor, height: '100%', transition: 'width 0.3s' }}></div>
                    </div>
                </div>
            );
        }}
    ];

    // =========================================================================
    // 3. SCHEMA DO MEDINA 2026 (O CÉREBRO DA IA)
    // =========================================================================
    const camposForm = useMemo(() => [
        { name: 'titulo_projeto', label: 'Título do Projeto (Ex: Dosagem CAUQ - Faixa C)', type: 'text', required: true, colSpan: 12 },
        { name: 'empresa_id', label: 'Construtora / Cliente Pagador', type: 'select', required: true, colSpan: 6, options: [
            { label: 'Selecione a Empresa...', value: '' }, ...empresasList
        ]},
        { name: 'obra_id', label: 'Rodovia / Trecho de Aplicação', type: 'select', required: true, colSpan: 6, options: [
            { label: 'Selecione a Obra...', value: '' }, ...obrasList
        ]},
        
        { name: 'divider_1', label: 'PARÂMETROS DE DIMENSIONAMENTO (MEDINA 2.0)', type: 'title', colSpan: 12 },
        
        { name: 'numero_n_projeto', label: 'Número N de Projeto (Eixos 8.2t)', type: 'text', required: true, colSpan: 4, placeholder: 'Ex: 6.00E+07' },
        { name: 'tipo_via', label: 'Classe da Via (Confiabilidade)', type: 'select', required: true, colSpan: 4, options: [
            { label: 'Sistema Arterial Principal (95%)', value: 'ARTERIAL_PRINCIPAL' },
            { label: 'Sistema Arterial Primário (85%)', value: 'ARTERIAL_PRIMARIO' },
            { label: 'Sistema Arterial Secundário (75%)', value: 'ARTERIAL_SECUNDARIO' },
            { label: 'Sistema Coletor Primário (85%)', value: 'COLETOR_PRIMARIO' },
            { label: 'Sistema Coletor Secundário (75%)', value: 'COLETOR_SECUNDARIO' },
            { label: 'Sistema Local (65%)', value: 'LOCAL' }
        ]},
        { name: 'condicao_trafego_fn', label: 'Condição de Tráfego (Flow Number)', type: 'select', required: true, colSpan: 4, options: [
            { label: 'NORMAL (V > 60km/h, Temp < 64ºC)', value: 'NORMAL' },
            { label: 'SEVERA (V < 60km/h, Interseções, Pedágios)', value: 'SEVERA' }
        ]},

        { name: 'divider_2', label: 'METAS DE DESEMPENHO E ESTRUTURA', type: 'title', colSpan: 12 },
        
        { name: 'tipo_mistura', label: 'Tipo de Mistura Asfáltica', type: 'select', required: true, colSpan: 4, options: [
            { label: 'CAUQ (Concreto Asfáltico)', value: 'CAUQ' },
            { label: 'SMA (Stone Matrix Asphalt)', value: 'SMA' },
            { label: 'CPA (Camada Porosa de Atrito)', value: 'CPA' },
            { label: 'Microrrevestimento', value: 'MICRO' }
        ]},
        { name: 'classe_fadiga_alvo', label: 'Classe de Fadiga Alvo', type: 'select', required: true, colSpan: 4, options: [
            { label: 'Classe 1 (N < 1E7)', value: 'CLASSE_1' },
            { label: 'Classe 2 (1E7 a 2E7)', value: 'CLASSE_2' },
            { label: 'Classe 3 (2E7 a 3E7)', value: 'CLASSE_3' },
            { label: 'Classe 4 (N > 3E7 - Requer Base Cimentada)', value: 'CLASSE_4' }
        ]},
        { name: 'tipo_base_subjacente', label: 'Material da Base Subjacente', type: 'select', required: true, colSpan: 4, options: [
            { label: 'Base Granular (Brita/Solo)', value: 'GRANULAR' },
            { label: 'Base Cimentada (BGTC/Solo-Cimento/CCR)', value: 'CIMENTADA' }
        ]}
    ], [empresasList, obrasList]);

    // =========================================================================
    // 4. LÓGICA DE PERSISTÊNCIA ATÔMICA
    // =========================================================================
    const handleSaveProjeto = async (formData) => {
        setIsProcessing(true);
        try {
            // Geração de ID corporativo bonito (Ex: DOS-2026-A1B2)
            const anoAtual = new Date().getFullYear();
            const hexRandom = Math.floor(Math.random() * 65535).toString(16).toUpperCase().padStart(4, '0');
            const novoId = `DOS-${anoAtual}-${hexRandom}`;

            const isEdicao = !!editingProjeto?.id;
            const docId = isEdicao ? editingProjeto.id : novoId;
            
            const projetoRef = doc(db, 'workspaces', workspaceData.cnpj_completo, 'crm_projetos_dosagem', docId);

            // Monta o payload garantindo status iniciais se for um projeto novo
            const payload = {
                ...formData,
                updated_at: serverTimestamp(),
                created_at: isEdicao ? editingProjeto.created_at : serverTimestamp(),
            };

            // Trava de estado inicial
            if (!isEdicao) {
                payload.status = 'CRIADO';
                payload.progresso = 0; // % de tickets de ensaio concluídos
            }

            await setDoc(projetoRef, payload, { merge: true });

            alert(`✅ Projeto de Dosagem ${docId} salvo. O Guarda-Chuva está pronto para receber ensaios.`);
            setView('LIST');
            carregarDadosBase();
        } catch (e) {
            alert("Erro crítico de banco de dados: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="pts-fade-in" style={{ padding: '30px', height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
            <header className="pts-view-header" style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 style={{ color: '#0f172a', margin: '0 0 5px 0', fontSize: '1.8rem', fontWeight: '800' }}>
                        Projetos de Dosagem (MeDiNa 2.0)
                    </h1>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.95rem' }}>
                        Orquestração de mix design. Defina as metas estruturais para que a IA valide os ensaios laboratoriais.
                    </p>
                </div>
                <div className="pts-header-actions">
                    <button 
                        className="pts-btn-primary" 
                        onClick={() => {
                            setEditingProjeto(null);
                            setView(view === 'LIST' ? 'FORM' : 'LIST');
                        }}
                    >
                        {view === 'LIST' ? '+ Novo Projeto de Dosagem' : '← Voltar para Painel'}
                    </button>
                </div>
            </header>

            {view === 'LIST' ? (
                <div style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', borderRadius: '12px' }}>
                    <TabelaCRM 
                        columns={colunas} 
                        data={projetos} 
                        onRowClick={(row) => {
                            setEditingProjeto(row);
                            setView('FORM');
                        }}
                        emptyMessage="Nenhum projeto ativo. Clique em '+ Novo Projeto de Dosagem' para estabelecer os critérios do MeDiNa."
                    />
                </div>
            ) : (
                <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', padding: '40px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)' }}>
                    <h3 style={{ borderBottom: '2px solid #f1f5f9', paddingBottom: '15px', marginBottom: '25px', color: '#0369a1' }}>
                        {editingProjeto ? `Editando Matriz: ${editingProjeto.id}` : 'Matriz de Requisitos MeDiNa'}
                    </h3>
                    <FormularioDinamico 
                        fields={camposForm} 
                        initialData={editingProjeto}
                        onSubmit={handleSaveProjeto}
                        onCancel={() => setView('LIST')}
                        isProcessing={isProcessing}
                        submitLabel={editingProjeto ? "Atualizar Metas" : "Abrir Dossiê de Dosagem"}
                    />
                </div>
            )}
        </div>
    );
};

export default ProjetosDosagem;